import { defineStore } from 'pinia'
import { ref } from 'vue'

export type DialogTone = 'default' | 'danger'

export interface DialogOptions {
  title?: string
  message: string
  confirmText?: string
  cancelText?: string
  tone?: DialogTone
  /** alert mode: only primary button */
  mode?: 'confirm' | 'alert'
}

interface DialogState extends Required<DialogOptions> {
  open: boolean
}

type Resolver = (value: boolean) => void

const defaultState = (): DialogState => ({
  open: false,
  title: '确认',
  message: '',
  confirmText: '确定',
  cancelText: '取消',
  tone: 'default',
  mode: 'confirm',
})

export const useDialogStore = defineStore('dialog', () => {
  const state = ref<DialogState>(defaultState())
  let resolveFn: Resolver | null = null

  function close(result: boolean) {
    state.value.open = false
    const r = resolveFn
    resolveFn = null
    r?.(result)
  }

  function open(options: DialogOptions): Promise<boolean> {
    // Resolve any pending dialog as cancel
    if (resolveFn) {
      resolveFn(false)
      resolveFn = null
    }
    state.value = {
      open: true,
      title: options.title ?? (options.mode === 'alert' ? '提示' : '确认'),
      message: options.message,
      confirmText: options.confirmText ?? '确定',
      cancelText: options.cancelText ?? '取消',
      tone: options.tone ?? 'default',
      mode: options.mode ?? 'confirm',
    }
    return new Promise<boolean>((resolve) => {
      resolveFn = resolve
    })
  }

  function confirm(options: DialogOptions | string): Promise<boolean> {
    const opts = typeof options === 'string' ? { message: options } : options
    return open({ ...opts, mode: 'confirm' })
  }

  function alert(options: DialogOptions | string): Promise<boolean> {
    const opts = typeof options === 'string' ? { message: options } : options
    return open({ ...opts, mode: 'alert', title: opts.title ?? '提示' })
  }

  function accept() {
    close(true)
  }

  function dismiss() {
    close(false)
  }

  return {
    state,
    confirm,
    alert,
    accept,
    dismiss,
  }
})
